import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getProducibleProducts } from '../../services/productionService';
import type { ProducibleProduct } from '../../types/production.types';

interface ProducibleProductAutocompleteProps {
  value: ProducibleProduct | null;
  onChange: (product: ProducibleProduct | null) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  initialProductId?: string | null;
  onInitialProduct?: (product: ProducibleProduct) => void;
}

const ProducibleProductAutocomplete: React.FC<ProducibleProductAutocompleteProps> = ({
  value,
  onChange,
  disabled,
  label,
  required = true,
  initialProductId,
  onInitialProduct,
}) => {
  const { t } = useTranslation('production');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ProducibleProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  const onInitialRef = useRef(onInitialProduct);
  onChangeRef.current = onChange;
  onInitialRef.current = onInitialProduct;

  useEffect(() => {
    if (!initialProductId || value?.productId === initialProductId) return undefined;
    let active = true;
    getProducibleProducts({ productId: initialProductId, page: 1, pageSize: 1 })
      .then((result) => {
        const found = (result.items ?? []).find((item) => item.productId === initialProductId) ?? (result.items ?? [])[0];
        if (!active || !found) return;
        const deliver = onInitialRef.current ?? onChangeRef.current;
        deliver(found);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [initialProductId, value?.productId]);

  useEffect(() => {
    if (disabled) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      getProducibleProducts({
        search: inputValue.trim() || undefined,
        page: 1,
        pageSize: 20,
      })
        .then((result) => {
          if (active) setOptions(result.items ?? []);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [inputValue, disabled]);

  const merged = useMemo(() => {
    if (!value) return options;
    if (options.some((option) => option.productId === value.productId)) return options;
    return [value, ...options];
  }, [options, value]);

  return (
    <Autocomplete
      value={value}
      disabled={disabled}
      options={merged}
      loading={loading}
      filterOptions={(listed) => listed}
      getOptionLabel={(option) => option.name || option.stockCode || option.productId}
      isOptionEqualToValue={(option, selected) => option.productId === selected.productId}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') setInputValue(next);
      }}
      onChange={(_, next) => onChange(next)}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.productId}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[option.stockCode, option.unit].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {option.productionMode ? (
            <Chip size="small" variant="outlined" label={t(`modes.${option.productionMode}`, { defaultValue: option.productionMode })} />
          ) : null}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          size="small"
          label={label ?? t('editor.product')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProducibleProductAutocomplete;
